/**
 * Offline Action Queue Manager (Day-15 Module 2)
 * 
 * Collection: offline_queue
 * 
 * Fields:
 * - id: string
 * - actionType: string
 * - payload: object
 * - createdAt: string (ISO date)
 * - status: 'PENDING' | 'SYNCED' | 'FAILED'
 * - retryCount: number
 */

import {
  STORES,
  generateOfflineId,
  addToStore,
  updateInStore,
  getAllFromStore,
  deleteFromStore,
  getByIndex,
} from './storage';

// ─────────────────────────────────────────────────────────────────
// Supported Action Types
// ─────────────────────────────────────────────────────────────────

export const ACTION_TYPES = {
  CHECKIN: 'CHECKIN',
  CHECKOUT: 'CHECKOUT',
  PROGRESS_NOTE: 'PROGRESS_NOTE',
  INCIDENT_REPORT: 'INCIDENT_REPORT',
  MEDICATION_LOG: 'MEDICATION_LOG',
  TASK_COMPLETE: 'TASK_COMPLETE',
  HANDOVER_NOTE: 'HANDOVER_NOTE',
  ATTACHMENT_UPLOAD: 'ATTACHMENT_UPLOAD',
  TRANSPORT_LOG_CREATE: 'TRANSPORT_LOG_CREATE',
  CHECKLIST_UPDATE: 'CHECKLIST_UPDATE',
  CHART_ENTRY: 'CHART_ENTRY',
  SAFETY_EVENT: 'SAFETY_EVENT',
};

// ─────────────────────────────────────────────────────────────────
// Action Statuses
// ─────────────────────────────────────────────────────────────────

export const ACTION_STATUS = {
  PENDING: 'PENDING',
  SYNCED: 'SYNCED',
  FAILED: 'FAILED',
};

// ─────────────────────────────────────────────────────────────────
// Queue Operations
// ─────────────────────────────────────────────────────────────────

/**
 * Queue an offline action
 * @param {string} actionType - Action type from ACTION_TYPES
 * @param {object} payload - Action data
 * @returns {object} - The queued action
 */
export async function queueOfflineAction(actionType, payload) {
  // Validate action type
  if (!Object.values(ACTION_TYPES).includes(actionType)) {
    throw new Error(`Invalid action type: ${actionType}`);
  }

  const action = {
    id: generateOfflineId(),
    actionType,
    payload,
    createdAt: new Date().toISOString(),
    status: ACTION_STATUS.PENDING,
    retryCount: 0,
  };

  await addToStore(STORES.OFFLINE_QUEUE, action);
  
  // Dispatch event for UI updates
  window.dispatchEvent(new CustomEvent('offlineQueueUpdated', { detail: { action } }));
  
  return action;
}

/**
 * Get all pending actions
 */
export async function getPendingActions() {
  const all = await getAllFromStore(STORES.OFFLINE_QUEUE);
  return all.filter(a => a.status === ACTION_STATUS.PENDING);
}

/**
 * Get all failed actions
 */
export async function getFailedActions() {
  const all = await getAllFromStore(STORES.OFFLINE_QUEUE);
  return all.filter(a => a.status === ACTION_STATUS.FAILED);
}

/**
 * Get all actions in queue (any status)
 */
export async function getAllQueuedActions() {
  return getAllFromStore(STORES.OFFLINE_QUEUE);
}

/**
 * Get queue statistics
 */
export async function getQueueStats() {
  const all = await getAllFromStore(STORES.OFFLINE_QUEUE);
  return {
    total: all.length,
    pending: all.filter(a => a.status === ACTION_STATUS.PENDING).length,
    synced: all.filter(a => a.status === ACTION_STATUS.SYNCED).length,
    failed: all.filter(a => a.status === ACTION_STATUS.FAILED).length,
  };
}

/**
 * Update action status
 */
export async function updateActionStatus(actionId, status, extra = {}) {
  const all = await getAllFromStore(STORES.OFFLINE_QUEUE);
  const action = all.find(a => a.id === actionId);
  
  if (!action) {
    throw new Error(`Action ${actionId} not found`);
  }
  
  const updated = {
    ...action,
    status,
    ...extra,
  };
  
  await updateInStore(STORES.OFFLINE_QUEUE, updated);
  window.dispatchEvent(new CustomEvent('offlineQueueUpdated', { detail: { action: updated } }));
  
  return updated;
}

/**
 * Mark action as synced
 */
export async function markActionSynced(actionId, serverId = null) {
  return updateActionStatus(actionId, ACTION_STATUS.SYNCED, {
    serverId,
    syncedAt: new Date().toISOString(),
  });
}

/**
 * Mark action as failed and increment retry count
 */
export async function markActionFailed(actionId, error) {
  const all = await getAllFromStore(STORES.OFFLINE_QUEUE);
  const action = all.find(a => a.id === actionId);
  
  if (!action) {
    throw new Error(`Action ${actionId} not found`);
  }
  
  return updateActionStatus(actionId, ACTION_STATUS.FAILED, {
    lastError: typeof error === 'string' ? error : error.message || 'Unknown error',
    retryCount: (action.retryCount || 0) + 1,
    lastFailedAt: new Date().toISOString(),
  });
}

/**
 * Retry a failed action (reset to pending)
 */
export async function retryAction(actionId) {
  return updateActionStatus(actionId, ACTION_STATUS.PENDING, {
    lastError: null,
  });
}

/**
 * Increment retry count for an action
 */
export async function incrementRetryCount(actionId) {
  const all = await getAllFromStore(STORES.OFFLINE_QUEUE);
  const action = all.find(a => a.id === actionId);
  
  if (!action) return null;
  
  const updated = {
    ...action,
    retryCount: (action.retryCount || 0) + 1,
    lastRetryAt: new Date().toISOString(),
  };
  
  await updateInStore(STORES.OFFLINE_QUEUE, updated);
  return updated;
}

/**
 * Delete an action from queue
 */
export async function deleteQueuedAction(actionId) {
  await deleteFromStore(STORES.OFFLINE_QUEUE, actionId);
  window.dispatchEvent(new CustomEvent('offlineQueueUpdated'));
}

/**
 * Remove all synced actions (cleanup)
 */
export async function cleanupSyncedActions() {
  const all = await getAllFromStore(STORES.OFFLINE_QUEUE);
  const synced = all.filter(a => a.status === ACTION_STATUS.SYNCED);
  
  for (const action of synced) {
    await deleteFromStore(STORES.OFFLINE_QUEUE, action.id);
  }
  
  window.dispatchEvent(new CustomEvent('offlineQueueUpdated'));
  return synced.length;
}

/**
 * Check if there are pending or failed actions
 */
export async function hasPendingActions() {
  const stats = await getQueueStats();
  return stats.pending > 0 || stats.failed > 0;
}

/**
 * Get actions by type
 */
export async function getActionsByType(actionType) {
  return getByIndex(STORES.OFFLINE_QUEUE, 'actionType', actionType);
}

// ─────────────────────────────────────────────────────────────────
// Human-Readable Helpers
// ─────────────────────────────────────────────────────────────────

/**
 * Get human-readable action description
 */
export function getActionDescription(action) {
  const descriptions = {
    [ACTION_TYPES.CHECKIN]: 'Shift Check-In',
    [ACTION_TYPES.CHECKOUT]: 'Shift Check-Out',
    [ACTION_TYPES.PROGRESS_NOTE]: 'Progress Note',
    [ACTION_TYPES.INCIDENT_REPORT]: 'Incident Report',
    [ACTION_TYPES.MEDICATION_LOG]: 'Medication Log',
    [ACTION_TYPES.TASK_COMPLETE]: 'Task Completion',
    [ACTION_TYPES.HANDOVER_NOTE]: 'Handover Note',
    [ACTION_TYPES.ATTACHMENT_UPLOAD]: 'File Upload',
    [ACTION_TYPES.CHART_ENTRY]: 'Clinical Chart',
    [ACTION_TYPES.SAFETY_EVENT]: 'Safety Alert',
  };
  return descriptions[action.actionType] || action.actionType;
}

/**
 * Get human-readable status
 */
export function getStatusDescription(status) {
  const descriptions = {
    [ACTION_STATUS.PENDING]: 'Waiting to sync',
    [ACTION_STATUS.SYNCED]: 'Synced successfully',
    [ACTION_STATUS.FAILED]: 'Sync failed',
  };
  return descriptions[status] || status;
}

/**
 * Get human-readable error message
 */
export function getReadableError(action) {
  if (!action.lastError) return null;
  
  const errorMappings = {
    'Network Error': 'No internet connection. Will retry when online.',
    'Failed to fetch': 'No internet connection. Will retry when online.',
    'timeout': 'Server took too long to respond. Will retry.',
    '401': 'Session expired. Please log in again.',
    '403': 'You don\'t have permission for this action.',
    '404': 'The record was not found on server.',
    '409': 'Conflict: This record was modified by someone else.',
    '422': 'Invalid data. Please review and try again.',
    '500': 'Server error. Our team has been notified.',
    '502': 'Server temporarily unavailable. Will retry.',
    '503': 'Server temporarily unavailable. Will retry.',
  };
  
  for (const [key, message] of Object.entries(errorMappings)) {
    if (action.lastError.includes(key)) return message;
  }
  
  return action.lastError;
}

/**
 * Get action icon name (for UI)
 */
export function getActionIcon(actionType) {
  const icons = {
    [ACTION_TYPES.CHECKIN]: 'Play',
    [ACTION_TYPES.CHECKOUT]: 'Square',
    [ACTION_TYPES.PROGRESS_NOTE]: 'FileText',
    [ACTION_TYPES.INCIDENT_REPORT]: 'AlertTriangle',
    [ACTION_TYPES.MEDICATION_LOG]: 'Pill',
    [ACTION_TYPES.TASK_COMPLETE]: 'CheckCircle',
    [ACTION_TYPES.HANDOVER_NOTE]: 'ClipboardList',
    [ACTION_TYPES.ATTACHMENT_UPLOAD]: 'Upload',
    [ACTION_TYPES.CHART_ENTRY]: 'ClipboardList',
    [ACTION_TYPES.SAFETY_EVENT]: 'ShieldAlert',
  };
  return icons[actionType] || 'Circle';
}

/**
 * Format timestamp for display
 */
export function formatActionTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-AU', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
}

/**
 * Format date for display
 */
export function formatActionDate(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
